/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { type ElementType, memo, type ReactNode, useCallback } from 'react';
import { styled, jsx } from '@compiled/react';
import isEqual from 'lodash/isEqual';
import { usePreloadedQuery, type PreloadedQuery } from 'react-relay';
import { graphql } from 'relay-runtime';
import { cssMap } from '@atlaskit/css';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { GlobalInvite } from '@atlassian/global-invite';
import { NAVIGATION_SIDEBAR_MARKS } from '@atlassian/jira-atlassian-navigation-performance-metrics/src/constants.tsx';
import { NavSidebarSSRPerformanceMark } from '@atlassian/jira-atlassian-navigation-performance-metrics/src/index.tsx';
import { layers } from '@atlassian/jira-common-styles/src/main.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { useFlagService } from '@atlassian/jira-flags';
import { useIntlV2 as useIntl } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import { useLeftSidebarState } from '@atlassian/jira-layout-controller/src/controllers/layout-controller/consumers/left-sidebar-controller/index.tsx';
import { useLayoutStoreActions } from '@atlassian/jira-layout-controller/src/controllers/layout-controller/store/index.tsx';
import { sidebarNav4UiStateResource } from '@atlassian/jira-navigation-apps-resources/src/services/sidebar-nav4/index.tsx';
import { SidebarStart } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/metrics/main.tsx';
import { SidebarNav4ContextProvider } from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/controllers/sidebar-context/index.tsx';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import {
	LEFT_SIDEBAR_EXPANDED_MIN_WIDTH,
	IS_LEFT_SIDEBAR_COLLAPSED_DEFAULT,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/constants/index.tsx';
import { SidebarSelectorProvider } from '@atlassian/jira-navigation-apps-sidebar-selector/src/index.tsx';
import {
	LeftSidebar,
	// @ts-expect-error - TS2305 - Module '"@atlassian/jira-navigation-system"' has no exported member 'ResizeButtonProps'.
	type ResizeButtonProps,
} from '@atlassian/jira-navigation-system/src/index.tsx';
import Placeholder, { PlaceholderNew } from '@atlassian/jira-placeholder/src/index.tsx';
import { useCurrentRoute } from '@atlassian/jira-platform-router-utils/src/index.tsx';
import { useRelayResource } from '@atlassian/jira-relay-utils/src/services/resources/index.tsx';
import type { sidebarSelectorQuery } from '@atlassian/jira-relay/src/__generated__/sidebarSelectorQuery.graphql';
import { RenderTracerMark } from '@atlassian/jira-render-tracer-analytics/src/main.tsx';
import { SidebarFeedbackButton } from '@atlassian/jira-sidebar-nav4-feedback-button/src/ui/index.tsx';
import { useSubProduct } from '@atlassian/jira-subproduct/src/index.tsx';
import { useIsAnonymous } from '@atlassian/jira-tenant-context-controller/src/components/is-anonymous/index.tsx';
import { useLocale } from '@atlassian/jira-tenant-context-controller/src/components/locale/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { PanelSplitter, SideNav, SideNavFooter } from '@atlassian/navigation-system';
import { AsyncNudgeBundledUsersFlagContainer } from '@atlassian/jira-nudge-bundled-users-to-join-connie/src/async.tsx';
import { SidebarEntryPointSelector } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebar-selector/src/ui/entrypoint/index.tsx';
import { LeftSidebarSkipLink } from '../page-container/page-container-skip-links/index.tsx';
import { messages } from './messages.tsx';
import { AsyncResizeButtonTooltip } from './resize-button-tooltip/index.tsx';

const footerStyles = cssMap({
	root: {
		backgroundColor: token('elevation.surface.raised'),
	},
});

const SidebarNewSuspense = () => {
	const isAnonymous = useIsAnonymous();

	const { queryReference } = useRelayResource(sidebarNav4UiStateResource);

	if (isAnonymous) {
		return (
			<UFOSegment name="nav4.sidebar.anonymous">
				<PlaceholderNew name="sidebar.anonymous">
					<SidebarInternalAnonymous />
				</PlaceholderNew>
			</UFOSegment>
		);
	}
	return (
		<UFOSegment name="nav4.sidebar">
			<PlaceholderNew name="sidebar">
				{queryReference ? <SidebarInternal queryReference={queryReference} /> : null}
			</PlaceholderNew>
		</UFOSegment>
	);
};

const SidebarNewNoSuspense = () => {
	const isAnonymous = useIsAnonymous();

	const { queryReference } = useRelayResource(sidebarNav4UiStateResource);

	if (isAnonymous) {
		return (
			<UFOSegment name="nav4.sidebar.anonymous">
				<SidebarInternalAnonymous />
			</UFOSegment>
		);
	}
	return (
		<UFOSegment name="nav4.sidebar">
			{queryReference ? <SidebarInternal queryReference={queryReference} /> : null}
		</UFOSegment>
	);
};

const SidebarNew = componentWithCondition(
	() => fg('afo-3421-remove-sidebar-nav4-suspense'),
	SidebarNewNoSuspense,
	SidebarNewSuspense,
);

const SidebarInternalAnonymous = () => {
	const { formatMessage } = useIntl();

	const defaultCollapsed = false;
	const defaultWidth = 340;

	const [, { setLeftSidebarState }] = useLayoutStoreActions();
	const onExpand = useCallback(
		({ screen }: { screen: 'desktop' | 'mobile' }) => {
			if (screen === 'desktop') {
				setLeftSidebarState({ isCollapsed: false });
			}
		},
		[setLeftSidebarState],
	);

	const onCollapse = useCallback(
		({ screen }: { screen: 'desktop' | 'mobile' }) => {
			if (screen === 'desktop') {
				setLeftSidebarState({ isCollapsed: true });
			}
		},
		[setLeftSidebarState],
	);

	const onResizeEnd = useCallback(
		({ finalWidth }: { finalWidth: number }) => {
			setLeftSidebarState({ width: finalWidth });
		},
		[setLeftSidebarState],
	);

	return (
		<SidebarNav4ContextProvider>
			<SideNav
				testId={TEST_ID}
				defaultCollapsed={defaultCollapsed}
				defaultWidth={defaultWidth}
				onExpand={onExpand}
				onCollapse={onCollapse}
			>
				<SidebarEntryPointSelector />
				<PanelSplitter
					label={formatMessage(messages.sideBarPanelSplitterLabel)}
					onResizeEnd={onResizeEnd}
					testId="page-container-v2.common.ui.sidebar.panel-splitter"
				/>
			</SideNav>
		</SidebarNav4ContextProvider>
	);
};

/**
 * This test ID is used in pollinator tests. Do not change lightly.
 */
const TEST_ID = 'page-layout.sidebar';

const SidebarInternal = ({
	queryReference,
}: {
	queryReference: PreloadedQuery<sidebarSelectorQuery>;
}) => {
	const { formatMessage } = useIntl();
	const { showFlag } = useFlagService();
	const subProduct = useSubProduct();

	const data = usePreloadedQuery<sidebarSelectorQuery>(
		graphql`
			query sidebarSelectorQuery($cloudId: ID!) {
				jira @required(action: THROW) {
					navigationUIState(cloudId: $cloudId) {
						isLeftSidebarCollapsed @required(action: THROW)
						leftSidebarWidth @required(action: THROW)
					}
				}
			}
		`,
		queryReference,
	);
	/*
		Backend may respond with an error with the reason as Navigation UI state user property does not exist
		under a scenario where a new user joins an existing Jira site.
		When this happens, UI would not have data coming through from sidebarSelectorQuery.
		So, default values are used in such a case.
	*/
	const defaultCollapsed =
		data?.jira?.navigationUIState?.isLeftSidebarCollapsed ?? IS_LEFT_SIDEBAR_COLLAPSED_DEFAULT;
	const defaultWidth =
		data?.jira?.navigationUIState?.leftSidebarWidth ?? LEFT_SIDEBAR_EXPANDED_MIN_WIDTH;

	const [, { setLeftSidebarState }] = useLayoutStoreActions();
	const onExpand = useCallback(
		({ screen }: { screen: 'desktop' | 'mobile' }) => {
			if (screen === 'desktop') {
				setLeftSidebarState({ isCollapsed: false });
			}
		},
		[setLeftSidebarState],
	);

	const onCollapse = useCallback(
		({ screen }: { screen: 'desktop' | 'mobile' }) => {
			if (screen === 'desktop') {
				setLeftSidebarState({ isCollapsed: true });
			}
		},
		[setLeftSidebarState],
	);

	const onResizeEnd = useCallback(
		({ finalWidth }: { finalWidth: number }) => {
			setLeftSidebarState({ width: finalWidth });
		},
		[setLeftSidebarState],
	);

	const locale = useLocale();
	const showPersistentInvite =
		// Ensure that we can turn off the locale check with a feature gate if needed
		// eslint-disable-next-line jira/ff/no-preconditioning
		fg('jira_global_invite_nav4') &&
		// Do not trigger an exposure on the experiment for non-English locales
		(locale?.toLowerCase().startsWith('en') ?? false) &&
		expVal('jira_global_invite_nav4_experiment', 'global_invite_enabled', false);

	return (
		<SidebarNav4ContextProvider>
			<SideNav
				testId={TEST_ID}
				defaultCollapsed={defaultCollapsed}
				defaultWidth={defaultWidth}
				onExpand={onExpand}
				onCollapse={onCollapse}
			>
				<SidebarEntryPointSelector />
				<SideNavFooter xcss={footerStyles.root}>
					{showPersistentInvite ? (
						<Flex gap="space.100">
							<Box xcss={globalInviteButtonStyles}>
								<GlobalInvite
									testId="page-container-v2.common.ui.sidebar.global-invite"
									product="jira"
									subProduct={subProduct ?? undefined}
									showFlag={showFlag}
								/>
							</Box>
							<SidebarFeedbackButton />
						</Flex>
					) : (
						<SidebarFeedbackButton />
					)}
				</SideNavFooter>
				<PanelSplitter
					label={formatMessage(messages.sideBarPanelSplitterLabel)}
					onResizeEnd={onResizeEnd}
					testId="page-container-v2.common.ui.sidebar.panel-splitter"
				/>
				{fg('cc-onboarding-nudge-confluence-bundled-users-gate') && (
					<AsyncNudgeBundledUsersFlagContainer />
				)}
			</SideNav>
		</SidebarNav4ContextProvider>
	);
};

const SIDEBAR_TEST_ID = 'ContextualNavigation';
const SIDEBAR_TRACE_KEY = 'sidebar';

const sidebarOverrides = {
	ResizeButton: {
		render: (Component: ElementType<ResizeButtonProps>, props: ResizeButtonProps) => (
			<JSErrorBoundary
				id="sidebar-resize-tooltip"
				packageName="page-container"
				fallback={() => <Component {...props} />}
			>
				<Placeholder name="side_bar_button" fallback={<Component {...props} />}>
					<AsyncResizeButtonTooltip isLeftSidebarCollapsed={props.isLeftSidebarCollapsed}>
						<Component {...props} />
					</AsyncResizeButtonTooltip>
				</Placeholder>
			</JSErrorBoundary>
		),
	},
} as const;

type SidebarProps = {
	sidebar: ReactNode;
};

const SidebarInternalOld = ({ sidebar }: SidebarProps) => {
	const { width, isCollapsed } = useLeftSidebarState();
	const route = useCurrentRoute();
	const intl = useIntl();

	const shouldNotRenderSidebarInServerSide = __SERVER__ && isCollapsed;

	return (
		<UFOSegment name="left-sidebar">
			<SidebarStyleOverrides>
				<JSErrorBoundary
					id="leftSidebarSkipLink"
					packageName="jiraPageContainer"
					fallback="unmount"
				>
					<LeftSidebarSkipLink />
				</JSErrorBoundary>
				<LeftSidebar
					isFixed
					width={width}
					testId={SIDEBAR_TEST_ID}
					resizeButtonLabel={intl.formatMessage(messages.sidebarNavigation)}
					collapsedState={isCollapsed ? 'collapsed' : 'expanded'}
					overrides={sidebarOverrides}
					id="ak-side-navigation"
				>
					{!shouldNotRenderSidebarInServerSide && (
						<>
							{route !== null && (
								<>
									<SidebarStart />
									<RenderTracerMark type="start" traceKey={SIDEBAR_TRACE_KEY} />
								</>
							)}
							{sidebar}
							{route !== null && (
								<>
									<NavSidebarSSRPerformanceMark metricKey={NAVIGATION_SIDEBAR_MARKS.renderSsrEnd} />
									<RenderTracerMark type="end" traceKey={SIDEBAR_TRACE_KEY} />
								</>
							)}
						</>
					)}
				</LeftSidebar>
				{fg('cc-onboarding-nudge-confluence-bundled-users-gate') && (
					<AsyncNudgeBundledUsersFlagContainer />
				)}
			</SidebarStyleOverrides>
		</UFOSegment>
	);
};

const SidebarInternalOldMemo = memo(SidebarInternalOld, isEqual);

// moved children as function outside the `Sidebar` so that the function is not re-created every time `Sidebar` is re-rendered.
const renderChildren = (sidebar: ReactNode) =>
	sidebar !== null ? <SidebarInternalOldMemo sidebar={sidebar} /> : null;

const SidebarOld = () => {
	const { shouldRender } = useLeftSidebarState();
	const route = useCurrentRoute();

	if (!shouldRender) return null;

	return (
		<SidebarSelectorProvider sidebar={route.navigation?.sidebar ?? null}>
			{renderChildren}
		</SidebarSelectorProvider>
	);
};

export const Sidebar = componentWithCondition(getWillShowNav4, SidebarNew, SidebarOld);

/**
 * In the @atlaskit/page-layout, resize control uses button for
 * the grab area. At the same time, legacy batch.css reset all
 * the button to have a box-shadow when focused. So when user
 * resizing the sidebar, they will see two blue lines around
 * the grab area.
 */
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SidebarStyleOverrides = styled.div<{}>({
	position: 'relative',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	zIndex: layers.leftSidebar,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	"& button[data-testid='ContextualNavigation-grab-area']:focus": {
		boxShadow: 'none',
	},
});

const globalInviteButtonStyles = xcss({
	flexGrow: 1,
});
